import { TabOption } from '@/components/chariot-ui';
import { useMemo } from 'react';

import {
  ASSET_DRAWER_TAB_LABELS,
  ASSET_DRAWER_TAB_OPTIONS,
  ASSET_DRAWER_TABS,
} from '@/sections/asset/constants';
import { Asset } from '@/types';

/**
 * Hook for managing Asset Drawer tab configuration
 *
 * @param asset - Optional asset for dynamic tab generation based on asset type
 * @returns Array of tab options for the drawer
 *
 * @example
 * ```typescript
 * const tabs = useAssetTabs(asset);
 * // Returns: [{ label: 'Overview', value: '' }, ...]
 * ```
 */
export function useAssetTabs(asset?: Asset): TabOption[] {
  return useMemo(() => {
    // Use base tab configuration from constants
    const baseTabs = [...ASSET_DRAWER_TAB_OPTIONS];

    // Add Affiliation tab only when asset has affiliation results
    const hasAffiliationData = asset?.history?.some(
      item => item.filePath && item.affiliationVerdict
    );

    if (hasAffiliationData) {
      // Insert before Notes tab (last position)
      const notesIndex = baseTabs.findIndex(
        tab => tab.value === ASSET_DRAWER_TABS.NOTES
      );
      baseTabs.splice(notesIndex >= 0 ? notesIndex : baseTabs.length, 0, {
        label: ASSET_DRAWER_TAB_LABELS[ASSET_DRAWER_TABS.AFFILIATION],
        value: ASSET_DRAWER_TABS.AFFILIATION,
      });
    }

    return baseTabs;
  }, [asset?.history]);
}
